import {
    PropsWithChildren,
    createContext,
    useContext,
    useEffect,
    useState
} from "react";

import { parse } from "cookie";

import { LocationData } from "./useGeoCurrency";

type OneTrustContextType = {
    analyticsOptOut: boolean;
    isConsentLoaded: boolean;
};

const isCPRA = (locationData?: LocationData): boolean => {
    const country = locationData?.countryIsoCode?.toUpperCase();
    const state = locationData?.regionIsoCode?.toUpperCase();
    return country == "US" && state == "CA";
};

/**
 * Extract OptanonConsent cookie value from cookie string or headers.
 * @param cookieString - Cookie string from request headers (SSR) or document.cookie (client)
 * @returns The OptanonConsent cookie value or undefined if not found
 */
export const extractOptanonConsentCookie = (
    cookieString?: string
): string | undefined => {
    if (!cookieString) {
        return undefined;
    }

    const cookies = parse(cookieString);
    return cookies["OptanonConsent"];
};

/**
 * Check if OneTrust wrapper has performance cookie enabled.
 * This works inconjunction with OneTrustProvider, which checks for updates to the OneTrust cookie.
 * If the cookie is not found or performance isn't explicitly set to not track, default to tracking.
 *
 * @param optanonConsentCookie - OptanonConsent cookie value (passed from window in client or extracted from headers in SSR)
 * @param locationData - User location data
 * @returns true if performance tracking is enabled, false otherwise
 */
export const isPerformanceOnetrustEnabled = (
    optanonConsentCookie?: string,
    locationData?: LocationData
): boolean => {
    if (optanonConsentCookie === null || optanonConsentCookie === undefined) {
        if (isCPRA(locationData)) {
            return false;
        }
        return true;
    }

    const params = new URLSearchParams(optanonConsentCookie);
    const groups = params.get("groups");
    if (groups === null) {
        return true;
    }

    const groupPairs = groups.split(",");
    return groupPairs.includes("2:1");
};

const OneTrustContext = createContext<OneTrustContextType>({
    analyticsOptOut: true,
    isConsentLoaded: false
});

/**
 * Custom hook to return OneTrustContext.
 * @returns @type {OneTrustContextType}
 */
export const useOneTrustContext = (): OneTrustContextType => {
    const context = useContext(OneTrustContext);
    if (!context) {
        throw new Error(
            "calls to useOneTrustContext must be wrapped in <OneTrustProvider>"
        );
    }

    return context;
};

/**
 * Check `window.OnetrustActiveGroups` every quater of sec to see if OneTrust has been initialized.
 * OneTrust script is referenced from the head HTML tag and sets the `window.OnetrustActiveGroups` and `OptanonConsent` cookie once it has been initialized.
 * Also monitors data layer for consent update events to detect when consent has been updated by user interaction.
 */
export const OneTrustProvider = ({ children }: PropsWithChildren) => {
    const [analyticsOptOut, setAnalyticsOptOut] = useState(true);
    // check if one trust consent is loaded to GTM data layer
    const [isConsentLoaded, setIsConsentLoaded] = useState(false);

    useEffect(() => {
        const checkOnetrustActiveGroups = () => {
            if (window.OnetrustActiveGroups === undefined) {
                return;
            }

            const currentAnalyticsOptOut =
                !window.OnetrustActiveGroups.includes(",2,");
            if (analyticsOptOut !== currentAnalyticsOptOut) {
                setAnalyticsOptOut(currentAnalyticsOptOut);
            }

            /**
             * Clear the interval once the OneTrust has been initialized and only recheck every 1 sec if the optout status is still the default (true).
             */
            clearInterval(setIntervalId);
            if (currentAnalyticsOptOut) {
                setIntervalId = setInterval(checkOnetrustActiveGroups, 1000);
            }
        };

        let setIntervalId = setInterval(checkOnetrustActiveGroups, 250);

        return () => {
            clearInterval(setIntervalId);
        };
    }, [analyticsOptOut]);

    useEffect(() => {
        // Monitor data layer for consent update events
        const checkDataLayerForConsentUpdates = () => {
            if (!window.dataLayer) {
                return;
            }

            const dataLayer = window.dataLayer;
            for (const event of dataLayer) {
                // Check for OneTrust consent update events which are arrays like
                if (event && event[0] && event[1]) {
                    if (event[0] === "consent" && event[1] === "update") {
                        setIsConsentLoaded(true);
                        clearInterval(dataLayerIntervalId);
                    }
                }
            }
        };

        // Check data layer for consent updates every 250ms
        const dataLayerIntervalId = setInterval(
            checkDataLayerForConsentUpdates,
            250
        );

        return () => {
            clearInterval(dataLayerIntervalId);
        };
    }, [isConsentLoaded]);

    return (
        <OneTrustContext.Provider value={{ analyticsOptOut, isConsentLoaded }}>
            {children}
        </OneTrustContext.Provider>
    );
};
